import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import lockr from 'lockr';
import axios from 'axios';
import { fetchApi, fetchApiAuth } from '../utils/api';
import { ResponseProps, GetUsersPayload, UserData } from './types';
import constants from '../constants';

const {
    LOCALSTORAGE_EXPIRES_KEY,
    AUTH_KEY,
} = constants;

export const registerUser = createAsyncThunk(
    'user/register',
    async (payload:{email: string; password: string; first_name: string; last_name: string;}) => {
        const { email, password, first_name, last_name } = payload;
        const response:ResponseProps = await fetchApi({
            method: 'POST',
            url: 'v1/register',
            body: {
                email,
                password,
                first_name,
                last_name,
            },
        });
        return response.data;
    },
);

export const loginUser = createAsyncThunk(
    'user/login',
    async (payload:{email: string; password: string;}, { rejectWithValue }) => {
        const { email, password } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'v1/login',
                body: { email, password },
            });
            if (response.data.token) {
                const authDate = new Date();
                authDate.setMinutes(authDate.getMinutes() + 30);
                lockr.set(LOCALSTORAGE_EXPIRES_KEY, authDate);
                lockr.set(AUTH_KEY, response.data.token);
            } else if (response.data.twoFactorToken) {
                lockr.set('twoFactorToken', response.data.twoFactorToken);
            }
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const forgotPassword = createAsyncThunk(
    'user/forgotPassword',
    async (payload:{email: string;}, { rejectWithValue }) => {
        const { email } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'v1/forgot-password',
                body: { email },
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }

    },
);

export const resetPassword = createAsyncThunk(
    'user/resetPassword',
    async (payload:{email: string|undefined; password: string; passwordConfirmation: string; token: string|undefined;}, thunkAPI) => {
        const { email, password, passwordConfirmation, token } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'v1/reset-password',
                body: {
                    email,
                    password,
                    password_confirmation: passwordConfirmation,
                    token,
                },
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
                return thunkAPI.rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateUser = createAsyncThunk(
    'user/updateUser',
    async (payload:UserData, thunkAPI) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/user/update',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
                return thunkAPI.rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateOtherUser = createAsyncThunk(
    'user/updateOtherUser',
    async (payload: Partial<UserData>, thunkAPI) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/user/update-other-user',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
                return thunkAPI.rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const logoutUser = createAsyncThunk(
    'user/logout',
    async () => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/logout',
            });
            lockr.rm(AUTH_KEY);
            lockr.rm(LOCALSTORAGE_EXPIRES_KEY);
            localStorage.clear();
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            lockr.rm(AUTH_KEY);
            lockr.rm(LOCALSTORAGE_EXPIRES_KEY);
            localStorage.clear();
            throw error;
        }
    },
);

export const twoFactorConfirmation = createAsyncThunk(
    'user/twoFactorConfirmation',
    async (payload:{code: string;}) => {
        const { code } = payload;
        try {
            const response:ResponseProps = await fetchApi({
                method: 'POST',
                url: 'v1/two-factor-confirmation',
                body: {
                    twoFactorToken: lockr.get('twoFactorToken'),
                    code,
                },
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const twoFactorResend = createAsyncThunk(
    'user/twoFactorResend',
    async () => {
        try {
            const response = await fetchApi({
                method: 'POST',
                url: 'v1/two-factor-resend',
                body: {
                    twoFactorToken: lockr.get('twoFactorToken'),
                },
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getUsers = createAsyncThunk(
    'user/get-users',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/user/get-users',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const createUser = createAsyncThunk(
    'user/create-user',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/user/create-user',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const deleteUser = createAsyncThunk(
    'user/delete-user',
    async (payload:any) => {
        try {
            await fetchApiAuth({
                method: 'POST',
                url: 'v1/user/delete-user',
                body: payload,
            });
            return payload;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);
